import { useCallback, useEffect, useState } from 'react';

import { SwapCalls } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router';

import ContactsUploadDialog from 'domains/contacts/components/ContactsUploadDialog';
import FileUploadButton from 'domains/contacts/components/FileUploadButton';
import useContactsFileUploadProps from 'domains/contacts/hooks/useContactsFileUploadProps';
import Button from 'domains/core/components/Button';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import { MultiSelectOption } from 'domains/core/components/MultiSelectDropdown';
import PageLayout from 'domains/core/components/PageLayout';
import SearchField from 'domains/core/components/SearchField';
import StatusFilterDropdown from 'domains/core/components/StatusFilterDropdown';
import useCampaigns from 'hooks/queries/useCampaigns';
import useContacts from 'hooks/queries/useContacts';
import usePaginatedCampaigns from 'hooks/queries/usePaginatedCampaigns';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useCurationsQueryParams from 'hooks/useCurationsQueryParams';
import useDebounce from 'hooks/useDebounce';
import { CampaignStatus, SortDirection, URLPaths } from 'models/enums';
import { ACCESS_CENTRE_LIST_PAGE_SIZE, ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS, SEARCH_DEBOUNCE_MS } from 'utils';

import CampaignsTable from './CampaignsTable';

const toLabel = (status: string): string =>
    status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\bab\b/g, 'A/B')
        .replace(/\b\w/g, (c) => c.toUpperCase());

const allCampaignStatusOptions: MultiSelectOption<CampaignStatus>[] = Object.values(CampaignStatus)
    .map((status) => ({ value: status, label: toLabel(status) }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const CampaignsPage = () => {
    const history = useHistory();
    const { canManageCurationsDrafts, canUploadContacts } = useAccessCentrePermissions();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
    const { statuses, changeViewParams } = useCurationsQueryParams();
    const { paginationFlag } = useFlags();

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(ACCESS_CENTRE_LIST_PAGE_SIZE);
    const [sortColumnState, setSortColumnState] = useState('last_modified_timestamp');
    const [sortDirectionState, setSortDirectionState] = useState(SortDirection.DESCENDING);

    const offset = (page - 1) * rowsPerPage;
    const useListPagination = Boolean(paginationFlag);

    const campaignStatusOptions = allCampaignStatusOptions;

    const legacyCampaignsQuery = useCampaigns({
        searchTerm: debouncedSearchTerm,
        statuses,
        reactQueryOptions: {
            enabled: !useListPagination,
            refetchInterval: useListPagination ? false : 30000,
        },
    });

    const paginatedCampaignsQuery = usePaginatedCampaigns({
        options: {
            searchTerm: debouncedSearchTerm,
            statuses,
            limit: rowsPerPage,
            offset,
            sortColumn: sortColumnState || undefined,
            sortDirection: sortColumnState ? sortDirectionState : undefined,
        },
        reactQueryOptions: {
            enabled: useListPagination,
            refetchInterval: useListPagination ? 30000 : false,
            keepPreviousData: true,
        },
    });

    const campaigns = useListPagination ? paginatedCampaignsQuery.data?.data ?? [] : legacyCampaignsQuery.data ?? [];
    const campaignsTotalCount = useListPagination ? paginatedCampaignsQuery.data?.total : undefined;
    const isCampaignsLoading = useListPagination ? paginatedCampaignsQuery.isLoading : legacyCampaignsQuery.isLoading;
    const isCampaignsRefetching = useListPagination
        ? paginatedCampaignsQuery.isRefetching
        : legacyCampaignsQuery.isRefetching;
    const isCampaignsSuccess = useListPagination ? paginatedCampaignsQuery.isSuccess : legacyCampaignsQuery.isSuccess;
    const refetchCampaigns = useListPagination ? paginatedCampaignsQuery.refetch : legacyCampaignsQuery.refetch;

    const totalPages = Math.max(1, Math.ceil((campaignsTotalCount ?? 0) / rowsPerPage));

    useEffect(() => {
        if (!useListPagination) {
            return;
        }
        setPage(1);
    }, [useListPagination, debouncedSearchTerm, statuses]);

    useEffect(() => {
        if (!useListPagination) {
            return;
        }
        setPage(1);
    }, [useListPagination, rowsPerPage]);

    useEffect(() => {
        if (!useListPagination) {
            return;
        }
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [useListPagination, page, totalPages]);

    const handleCampaignSort = useCallback((sortColumn: string, direction: SortDirection) => {
        if (!sortColumn) {
            setSortColumnState('last_modified_timestamp');
            setSortDirectionState(SortDirection.DESCENDING);
        } else {
            setSortColumnState(sortColumn);
            setSortDirectionState(direction);
        }
        setPage(1);
    }, []);
    const { data: contacts, isSuccess: isContactsSuccess } = useContacts({
        options: { limit: '1' },
    });

    const fileUploadProps = useContactsFileUploadProps();

    const isContactsEmpty = !contacts?.length;

    const handleStatusFilterApply = (newStatuses: CampaignStatus[]) => {
        changeViewParams({ newStatuses });
    };

    if (isContactsSuccess && isContactsEmpty) {
        return (
            <EmptyStateLayout
                caption="curations"
                header="create authentic connections"
                guidingText="Once your contacts are uploaded, build your first Curation to begin communicating with your contacts."
            >
                {canUploadContacts && (
                    <>
                        <ContactsUploadDialog {...fileUploadProps} />
                        <FileUploadButton {...fileUploadProps} />
                    </>
                )}
            </EmptyStateLayout>
        );
    }

    return (
        <PageLayout
            header="curations"
            headerAction={
                canManageCurationsDrafts ? (
                    <Button
                        endIcon={<SwapCalls />}
                        onClick={() => history.push(URLPaths.CURATIONS_LANDING)}
                        variant="outlined"
                        data-testid="curations-create-button"
                    >
                        Create
                    </Button>
                ) : undefined
            }
            headerIcon={<SwapCalls fontSize="inherit" />}
            headerOthers={
                <Stack direction="row" spacing={4} alignItems="center">
                    <StatusFilterDropdown
                        options={campaignStatusOptions}
                        items={campaigns ?? []}
                        selectedValues={statuses}
                        onApply={handleStatusFilterApply}
                        label="Status"
                        testIdPrefix="campaign-status-filter"
                        menuId="campaign-status-filter-menu"
                        maxHeight="400px"
                        disabled={isCampaignsLoading || isCampaignsRefetching}
                    />
                    <SearchField
                        placeholder="Search curations..."
                        value={searchTerm}
                        onChange={setSearchTerm}
                        disabled={isCampaignsLoading || isCampaignsRefetching}
                        dataTestId="curations-search-input"
                    />
                </Stack>
            }
        >
            {(isCampaignsLoading || isCampaignsSuccess) && (
                <CampaignsTable
                    campaigns={campaigns ?? []}
                    isLoading={isCampaignsLoading}
                    isRefetching={isCampaignsRefetching}
                    searchTerm={debouncedSearchTerm}
                    statuses={statuses}
                    refetch={refetchCampaigns}
                    totalCampaignCount={useListPagination ? campaignsTotalCount : undefined}
                    pagination={
                        useListPagination
                            ? {
                                  page,
                                  totalPages,
                                  onPreviousPage: () => setPage((p) => Math.max(1, p - 1)),
                                  onNextPage: () => setPage((p) => Math.min(totalPages, p + 1)),
                                  rowsPerPage,
                                  rowsPerPageOptions: ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS,
                                  onRowsPerPageChange: (n) => {
                                      setRowsPerPage(n);
                                      setPage(1);
                                  },
                              }
                            : undefined
                    }
                    onCampaignSort={useListPagination ? handleCampaignSort : undefined}
                    manualCampaignSortBy={useListPagination}
                />
            )}
        </PageLayout>
    );
};

export default CampaignsPage;
